import React from 'react';

import AudioMessageIcon from '@ad-scripts/presentation/components/common/icons/audio_message';
import DataDipIcon from '@ad-scripts/presentation/components/common/icons/data_dip';
import ForwardIcon from '@ad-scripts/presentation/components/common/icons/forward';
import KeypadIcon from '@ad-scripts/presentation/components/common/icons/key_pad';
import MenuIcon from '@ad-scripts/presentation/components/common/icons/menu';
import NumberIcon from '@ad-scripts/presentation/components/common/icons/number';
import RouteToInboxIcon from '@ad-scripts/presentation/components/common/icons/route_to_inbox';
import SMSIcon from '@ad-scripts/presentation/components/common/icons/sms';
import ScheduleIcon from '@ad-scripts/presentation/components/common/icons/schedule';
import SplitStrokeIcon from '@ad-scripts/presentation/components/common/icons/stroke/split-stroke';
import StartIcon from '@ad-scripts/presentation/components/common/icons/fill/start_fill_icon';
import VariableIcon from '@ad-scripts/presentation/components/common/icons/variable';
import VariableMatchIcon from '@ad-scripts/presentation/components/common/icons/variable_match';
import VoiceMailIcon from '@ad-scripts/presentation/components/common/icons/voice_mail';
import SparkleLightIcon from '@ad-scripts/presentation/components/common/icons/sparkle_light';
import NODE_TYPES from 'src/models/Ivrs/NodeTypes';

const GENERIC_NODE = {
  allowToCreateNew: false,
  color: '#767676',
  description: 'Generic node used when a concrete implementation is not available',
  icon: StartIcon,
  label: 'Generic Node',
  treeNodeLabel: 'Generic',
};

const NODES_MAPPER = Object.freeze({
  [NODE_TYPES.START_NODE]: {
    allowToCreateNew: false,
    color: '#767676',
    description: '',
    icon: StartIcon,
    label: 'Start',
    treeNodeLabel: 'Start',
  },
  [NODE_TYPES.IVR_NODE_HTTP]: {
    allowToCreateNew: false,
    createNew: { baseName: 'New data dip', initial: {} },
    color: '#767676',
    icon: DataDipIcon,
    label: 'Data Dip Node',
    treeNodeLabel: 'Data Dip',
    description:
      'A way for you to collect input from the customer, dip into another system to output data to the customer.',
  },
  [NODE_TYPES.IVR_NODE_HTTP2]: {
    allowToCreateNew: true,
    createNew: { baseName: 'New HTTP', initial: { request: { method: 'GET', urlTemplate: '', headerTemplates: [] } } },
    color: '#767676',
    icon: DataDipIcon,
    label: 'HTTP Node',
    treeNodeLabel: 'HTTP',
    description: 'Interact with applications outside of Gladly',
  },
  [NODE_TYPES.IVR_NODE_FORWARD]: {
    allowToCreateNew: true,
    color: '#125715',
    icon: ForwardIcon,
    label: 'Forward Node',
    treeNodeLabel: 'Forward',
    description: 'Route your customers to an external number',
    createNew: {
      baseName: 'New forward',
      initial: { number: { extension: '', phoneNumber: '' } },
    },
  },
  [NODE_TYPES.IVR_NODE_INPUT]: {
    allowToCreateNew: true,
    color: '#767676',
    icon: KeypadIcon,
    label: 'Customer Input Node',
    treeNodeLabel: 'Input',
    description: 'Let customers enter information via number keys or speech',
    createNew: {
      baseName: 'New input',
      initial: {
        nextId: '',
        numDigits: 1,
        timeout: 5000000000,
        type: 'DTMF_SPEECH',
      },
    },
  },
  [NODE_TYPES.IVR_NODE_MESSAGE]: {
    allowToCreateNew: true,
    color: '#B20000',
    description: 'Play an audio or text to speech recording of your choice',
    icon: AudioMessageIcon,
    label: 'Message Node',
    treeNodeLabel: 'Message',
    createNew: {
      baseName: 'New message',
      initial: { message: { type: 'TEXT', message: '' }, nextId: '' },
    },
  },
  [NODE_TYPES.IVR_NODE_MENU]: {
    allowToCreateNew: true,
    color: '#4F2A93',
    icon: MenuIcon,
    label: 'Menu Node',
    treeNodeLabel: 'Menu',
    description: 'Set up paths based on numbers (DTMF) or speech (voice)',
    createNew: {
      baseName: 'New menu',
      initial: {
        message: { type: 'TEXT', message: '' },
        menu: [],
        language: 'en-US',
        badSelectionNodeId: '',
        speechTimeout: 5000000000,
      },
    },
  },
  [NODE_TYPES.IVR_NODE_NUMBER_MATCH]: {
    allowToCreateNew: false,
    color: '#767676',
    icon: NumberIcon,
    label: 'Customer Phone Number Matcher',
    treeNodeLabel: 'Match Number',
    description: 'Matches the phone number, which you can tie to a node like data dip to gather customer information.',
  },
  [NODE_TYPES.IVR_NODE_DIAL]: {
    allowToCreateNew: true,
    color: '#125715',
    icon: RouteToInboxIcon,
    label: 'Exit Node',
    treeNodeLabel: 'Exit',
    description: 'Route customers from this IVR to a corresponding Inbox',
    createNew: {
      baseName: 'New exit',
      initial: { description: '\u200B', selection: '' },
    },
  },
  [NODE_TYPES.IVR_NODE_SCHEDULE]: {
    allowToCreateNew: true,
    color: '#AA91FF',
    icon: ScheduleIcon,
    label: 'Schedule Node',
    treeNodeLabel: 'Schedule',
    description: 'Route customers based on your holiday or business hours',
    createNew: {
      baseName: 'New schedule',
      initial: { nodeSchedules: [], defaultNextId: '' },
    },
  },
  [NODE_TYPES.IVR_NODE_SCHEDULE_TEMPORARY_SPLIT]: {
    allowToCreateNew: true,
    color: '#009b00',
    icon: SplitStrokeIcon,
    label: 'Temporary Split Node',
    treeNodeLabel: 'Temporary Split',
    description: 'Close sections in the IVR to redirect customers to another path',
    createNew: {
      baseName: 'New temporary split',
      initial: { nodeSchedules: [], defaultNextId: '', defaultOffId: '' },
    },
  },
  [NODE_TYPES.IVR_NODE_SMS]: {
    allowToCreateNew: true,
    color: '#F98484',
    icon: SMSIcon,
    label: 'SMS Node',
    treeNodeLabel: 'SMS',
    description: 'The SMS node allows you to send an SMS message to your customer.',
    createNew: {
      baseName: 'New sms',
      initial: {
        body: '',
        errorNodeId: '',
        nextId: '',
        toNumber: '',
      },
    },
  },
  [NODE_TYPES.IVR_NODE_VARIABLE]: {
    allowToCreateNew: true,
    color: '#767676',
    icon: VariableIcon,
    label: 'Global IVR Variable',
    treeNodeLabel: 'Variable',
    description:
      'Create a variable to be used by other nodes throughout the IVR. Variables can be used anywhere template definitions are supported.',
    createNew: {
      baseName: 'New variable',
      initial: {
        errorNodeId: '',
        expression: '',
        nextId: '',
      },
    },
  },
  [NODE_TYPES.IVR_NODE_VARIABLE_MATCH]: {
    allowToCreateNew: true,
    color: '#767676',
    icon: VariableMatchIcon,
    label: 'Variable Node',
    treeNodeLabel: 'Variable Match',
    description:
      'Enables conditional IVR flow. The functionality is similar to a switch statement available in many programming languages.',
    createNew: {
      baseName: 'New variable match',
      initial: {
        errorNodeId: '',
        nextId: '',
      },
    },
  },
  [NODE_TYPES.IVR_NODE_VOICEMAIL]: {
    allowToCreateNew: true,
    color: '#F46262',
    icon: VoiceMailIcon,
    label: 'Voicemail Node',
    treeNodeLabel: 'Voicemail',
    description: 'Let customers leave a voicemail',
    createNew: {
      baseName: 'New voicemail',
      initial: { description: '\u200B' },
    },
  },
  [NODE_TYPES.IVR_NODE_VOICE_AUTOMATION]: {
    allowToCreateNew: true,
    color: '#8C69F0',
    icon: SparkleLightIcon,
    label: 'Sidekick Voice',
    treeNodeLabel: 'Sidekick Voice',
    description: (
      <>
        Enable Sidekick voice automation in your IVR. Once this configuration is saved, go to to{' '}
        <a
          href="/sidekick/entry-points"
          onClick={evt => {
            evt.preventDefault();
            window.location.assign('/sidekick/entry-points');
          }}
        >
          Sidekick Entry Points
        </a>{' '}
        connect this IVR node with your chosen Sidekick Profile.
      </>
    ),
    createNew: {
      baseName: 'New Sidekick Voice',
      initial: { description: '\u200B', record: false },
    },
  },
  [NODE_TYPES.IVR_NODE_HANDOFF]: {
    allowToCreateNew: true,
    color: '#8C69F0',
    icon: RouteToInboxIcon,
    label: 'Handoff',
    treeNodeLabel: 'Handoff',
    description: 'Route an automated call from this IVR to a corresponding Inbox',
    createNew: {
      baseName: 'New automation handoff',
      initial: { description: '\u200B', customerId: '{{.automation.customerId}}' },
    },
  },
});

export default new Proxy(NODES_MAPPER, {
  get: (target, key) => {
    if (key in target) {
      return target[key];
    }
    return GENERIC_NODE;
  },
});
